import React from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Icon,
  InputAdornment,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { V4_BUTTON_DEFAULT, V4_OUTLINED_DEFAULT, V4_TABLE } from '../../theme/v4Compat';
import { useTranslation } from 'react-i18next';
import ClearIcon from '@mui/icons-material/Clear';
import { Skeleton } from '../../elements/ui/skeleton';
import NoDataMessage from '../shared/NoDataMessage';
import { ApiService } from '../../hooks';
import { HealthSystemDomainLocationsResultLocationViewModel } from '../../api/HealthSystemApi';
import { DEFAULT_ROWS_PAGE_OPTIONS } from '../../constants/constants';
import { LocationStable, LocationStableTranslations } from '../../models/locations';

export interface LocationPickerResult {
  ids: string[];
  labels: Record<string, string>; // id → display label
}

interface LocationPickerDialogProps {
  open: boolean;
  selectedIds: string[];
  onClose: () => void;
  onConfirm: (result: LocationPickerResult) => void;
}

const LocationPickerDialog: React.FC<LocationPickerDialogProps> = ({ open, selectedIds, onClose, onConfirm }) => {
  const { t } = useTranslation();
  const apiService = new ApiService();

  const [search, setSearch] = React.useState('');
  const [locations, setLocations] = React.useState<HealthSystemDomainLocationsResultLocationViewModel[]>([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [isBusy, setIsBusy] = React.useState(false);
  const [stableFilter, setStableFilter] = React.useState<LocationStable | ''>('');

  // Working copy of selected IDs — committed only on confirm
  const [pendingIds, setPendingIds] = React.useState<string[]>([]);
  // Accumulate labels for all locations we've seen so we can display them in chips
  const [labelMap, setLabelMap] = React.useState<Record<string, string>>({});

  React.useEffect(() => {
    if (open) {
      setPendingIds(selectedIds);
      setLabelMap({});
      setSearch('');
      setStableFilter('');
      setPage(0);
    }
  }, [open]);

  React.useEffect(() => {
    if (open) {
      fetchLocations();
    }
  }, [open, page, rowsPerPage, stableFilter]);

  const fetchLocations = (searchTerm = search) => {
    setIsBusy(true);
    apiService.api.locations
      .locationsSearchLocations({
        Number: searchTerm || undefined,
        Room: searchTerm || undefined,
        Stable: stableFilter !== '' ? stableFilter : undefined,
        Page: page + 1,
        Size: rowsPerPage
      })
      .then((result) => {
        const data = result.data.data ?? [];
        setLocations(data);
        setTotal(result.data.total ?? 0);
        setLabelMap((prev) => {
          const next = { ...prev };
          data.forEach((loc) => {
            next[loc.id] = getLocationLabel(loc);
          });
          return next;
        });
      })
      .catch(() => {
        // fail silently
      })
      .finally(() => setIsBusy(false));
  };

  const handleSearch = () => {
    setPage(0);
    fetchLocations(search);
  };

  const handleToggle = (locId: string) => {
    setPendingIds((prev) => (prev.includes(locId) ? prev.filter((id) => id !== locId) : [...prev, locId]));
  };

  const getLocationLabel = (loc: HealthSystemDomainLocationsResultLocationViewModel): string => {
    const parts = [loc.number, loc.room, loc.pen].filter(Boolean);
    return parts.join(' · ') || loc.id;
  };

  const visibleIds = locations.map((l) => l.id);
  const visibleSelectedCount = visibleIds.filter((id) => pendingIds.includes(id)).length;
  const allVisibleSelected = visibleIds.length > 0 && visibleSelectedCount === visibleIds.length;

  const handleSelectAll = () => {
    if (allVisibleSelected) {
      setPendingIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
    } else {
      setPendingIds((prev) => [...prev, ...visibleIds.filter((id) => !prev.includes(id))]);
    }
  };

  const stableEntries = Object.values(LocationStable).filter(
    (v) => typeof v === 'number' && v !== LocationStable.Unknown
  ) as LocationStable[];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('f-silo-linked-locations')}</DialogTitle>

      <DialogContent dividers>
        {/* Search row */}
        <div className="mb-2 flex items-center gap-2">
          <TextField
            size="small"
            variant="outlined"
            fullWidth
            placeholder={`${t('f-chip-number')} / ${t('f-loc-room')}`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            slotProps={{
              input: {
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <ClearIcon
                      fontSize="small"
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        setSearch('');
                        setPage(0);
                        fetchLocations('');
                      }}
                    />
                  </InputAdornment>
                ) : undefined
              }
            }}
          />
          <Button variant="contained" color="primary" onClick={handleSearch} startIcon={<Icon>search</Icon>}>
            {t('f-search')}
          </Button>
        </div>

        {/* Stable filter + Select All row */}
        <div className="mb-4 flex items-center gap-2">
          <FormControl size="small" variant="outlined" style={{ minWidth: 180 }}>
            <Select
              value={stableFilter}
              onChange={(e) => {
                setStableFilter(e.target.value as LocationStable | '');
                setPage(0);
              }}
              displayEmpty
            >
              <MenuItem value="">
                {t('f-loc-stable')}: {t('f-com-all')}
              </MenuItem>
              {stableEntries.map((stable) => (
                <MenuItem key={stable} value={stable}>
                  {t(LocationStableTranslations[stable])}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            color="inherit"
            sx={V4_OUTLINED_DEFAULT}
            size="small"
            disabled={visibleIds.length === 0}
            onClick={handleSelectAll}
          >
            {allVisibleSelected ? t('f-com-deselect-all') : t('f-com-select-all')}
          </Button>

          {pendingIds.length > 0 && (
            <Typography variant="caption" color="textSecondary" style={{ marginLeft: 'auto' }}>
              {t('f-com-selected')}: {pendingIds.length}
            </Typography>
          )}
        </div>

        <TableContainer style={{ maxHeight: 360 }}>
          <Table size="small" stickyHeader sx={PICKER_TABLE}>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>{t('f-chip-number')}</TableCell>
                <TableCell>{t('f-loc-stable')}</TableCell>
                <TableCell>{t('f-loc-room')}</TableCell>
                <TableCell>{t('f-loc-pen')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isBusy &&
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {[0, 1, 2, 3, 4].map((j) => (
                      <TableCell key={j}>
                        <Skeleton className="h-4 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}

              {!isBusy &&
                locations.map((loc) => (
                  <TableRow key={loc.id} hover onClick={() => handleToggle(loc.id)} style={{ cursor: 'pointer' }}>
                    <TableCell padding="checkbox">
                      <Checkbox checked={pendingIds.includes(loc.id)} color="primary" />
                    </TableCell>
                    <TableCell>{loc.number || '-'}</TableCell>
                    <TableCell>
                      {loc.stable != null && loc.stable !== LocationStable.Unknown
                        ? t(LocationStableTranslations[loc.stable as LocationStable])
                        : '-'}
                    </TableCell>
                    <TableCell>{loc.room || '-'}</TableCell>
                    <TableCell>{loc.pen || '-'}</TableCell>
                  </TableRow>
                ))}

              {!isBusy && locations.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <NoDataMessage text={t('f-no-data')} />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={DEFAULT_ROWS_PAGE_OPTIONS}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_e, p) => setPage(p)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </DialogContent>

      <DialogActions>
        <Button color="inherit" sx={V4_BUTTON_DEFAULT} onClick={onClose}>
          {t('f-com-cancel')}
        </Button>
        <Button variant="contained" color="primary" onClick={() => onConfirm({ ids: pendingIds, labels: labelMap })}>
          {t('f-com-save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LocationPickerDialog;

/** v4's table padding, and its sticky header on the page background (v9 paints it white). */
const PICKER_TABLE = { ...V4_TABLE, '& .MuiTableCell-stickyHeader': { backgroundColor: '#fafafa' } } as const;
